import React, { useState, useRef, useLayoutEffect } from 'react'
import { Box, Typography, Divider } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import BulkDownloadLabelsB2C from './download/BulkDownloadLabelsB2C'
import BulkInvoiceDownload from './download/BulkInvoiceDownload'
import InvoiceLabelMergeDownload from './download/InvoiceLabelMergeDownload'
import Manifest from './list2/Manifest'
import PageTitle from '../../common/PageTitle'
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography'
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, withOpacity } from '../../styles/foundation/colors'
import { pageContainerSx } from '../../styles/patterns'

// Single entry point for these D2C download/manifest flows, previously separate sidebar
// entries (/download-labels-b2c, /download-invoice, /download-invoice-label-merge-b2c,
// /pending-pickups). Those paths stay routable (hidden from the sidebar) so bookmarks
// keep resolving to the same screens.
const TABS = [
  { key: 'label', label: 'Label', icon: <LabelOutlinedIcon />, color: PRIMARY_BLUE },
  { key: 'invoices', label: 'Invoices', icon: <ReceiptLongOutlinedIcon />, color: SUCCESS },
  { key: 'merge', label: 'Bulk Download Label and Invoice (Merged File)', icon: <LayersOutlinedIcon />, color: '#ff9800' },
  { key: 'manifest', label: 'Manifest', icon: <PendingActionsIcon />, color: '#7c4dff' },
]

const LabelsInvoices = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const initial = TABS.findIndex((t) => t.key === searchParams.get('tab'))
  const [active, setActive] = useState(initial >= 0 ? initial : 0)

  const handleTab = (i) => {
    setActive(i)
    // Keep the tab in the URL so a specific download screen can be linked to / reloaded into.
    setSearchParams({ tab: TABS[i].key }, { replace: true })
  }

  // The Manifest tab has its own sub-tabs, drawn as a tray that branches from this active
  // tab. Tabs are unequal width, so measure the active tab's centre (relative to the bar) and
  // hand it down as caretX so the connector/caret sits exactly under it — for whichever tab
  // is active, not a fixed position.
  const barRef = useRef(null)
  const tabRefs = useRef([])
  const [caretX, setCaretX] = useState(null)
  useLayoutEffect(() => {
    const measure = () => {
      const bar = barRef.current
      const el = tabRefs.current[active]
      if (!bar || !el) return
      const b = bar.getBoundingClientRect()
      const t = el.getBoundingClientRect()
      setCaretX(t.left + t.width / 2 - b.left)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [active])

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle="Download shipping labels and tax invoices, or manage pickup manifests">Labels, Invoices &amp; Manifests</PageTitle>

      {/* Download tabs */}
      <Box ref={barRef} sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', mb: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex' }}>
          {TABS.map((t, i) => {
            const isActive = active === i
            return (
              <React.Fragment key={t.key}>
                {i > 0 && <Divider orientation='vertical' flexItem sx={{ borderColor: BORDER }} />}
                <Box
                  ref={(el) => (tabRefs.current[i] = el)}
                  onClick={() => handleTab(i)}
                  sx={{
                    flex: '1 1 auto', minWidth: 130, py: 1.25, px: 2, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                    borderBottom: isActive ? `3px solid ${t.color}` : '3px solid transparent',
                    bgcolor: isActive ? withOpacity(t.color, 0.06) : 'transparent',
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: withOpacity(t.color, 0.04) },
                  }}
                >
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                    bgcolor: isActive ? withOpacity(t.color, 0.12) : withOpacity(t.color, 0.07),
                    color: isActive ? t.color : SUBTLE,
                    '& .MuiSvgIcon-root': { fontSize: 17 },
                  }}>
                    {t.icon}
                  </Box>
                  <Typography sx={{
                    fontFamily: FONT, fontSize: SIZE['2xs'],
                    fontWeight: isActive ? WEIGHT.bold : WEIGHT.medium,
                    color: isActive ? NAVY : SUBTLE, lineHeight: 1.2,
                  }}>
                    {t.label}
                  </Typography>
                </Box>
              </React.Fragment>
            )
          })}
        </Box>
      </Box>

      {/* Tab content — each screen keeps its own PageTitle, APIs and internal layout */}
      {active === 0 && (
        <BulkDownloadLabelsB2C
          embedded
          caretX={caretX}
          parentColor={TABS[0].color}
          parentLabel="Label"
          parentIcon={<LabelOutlinedIcon />}
        />
      )}
      {active === 1 && <BulkInvoiceDownload embedded />}
      {active === 2 && <InvoiceLabelMergeDownload type='B2C' embedded />}
      {active === 3 && (
        <Manifest
          embedded
          caretX={caretX}
          parentColor={TABS[3].color}
          parentLabel="Manifest"
          parentIcon={<PendingActionsIcon />}
        />
      )}
    </Box>
  )
}

export default LabelsInvoices
